"use client";

import React, { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-5 w-16 animate-pulse rounded-md bg-muted" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-medium text-foreground uppercase tracking-widest transition-colors hover:text-primary font-sans">
                {item.label}
                <span className="absolute -bottom-1.5 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-md">
      <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
        {loading ? <div className="flex flex-col gap-4">
            {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-8 w-32 animate-pulse rounded-md bg-muted" />)}
          </div> : <nav className="flex flex-col gap-4">
            {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-default uppercase tracking-widest text-foreground hover:text-primary transition-colors" onClick={() => setIsOpen(false)}>
                {item.label}
              </Link>)}
          </nav>}
        <div className="flex flex-col gap-3 pt-4 border-t border-border">
          <Button variant="outline" className="w-full rounded-full border-primary text-primary hover:bg-primary/5 justify-center font-sans">
            <Phone className="size-4 mr-2" />
            Contact
          </Button>
          <Button className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 justify-center font-sans">
            Get a Quote
          </Button>
        </div>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header data-section-id="224473" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md shadow-md" : "bg-background")}>
      <MenuProvider menu_id="16">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex h-20 items-center justify-between">
            {}
            <div className="flex items-center mr-8">
              <Link to="/HomePage#" className="flex items-center">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1656752/img/av_masonry_logo.png" alt="AV MASONRY LTD." className="h-7 w-auto" />
              </Link>
            </div>

            {}
            <div className="flex-1 flex justify-start">
              <DesktopMenu />
            </div>

            {}
            <div className="hidden md:flex items-center gap-4">
              <Button variant="outline" className="rounded-full border-primary text-primary hover:bg-primary/5 hover:text-primary transition-colors h-10 px-6 font-sans">
                <Phone className="size-4 mr-2" />
                Contact
              </Button>
              <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors h-10 px-8 shadow-none font-sans">
                Get a Quote
              </Button>
            </div>

            {}
            <div className="md:hidden flex items-center">
              <Button variant="ghost" size="icon" className="text-foreground" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
                {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
              </Button>
            </div>
          </div>
        </div>

        {}
        <MobileMenu isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
      </MenuProvider>
    </header>;
}